import { isAuthenticated, setAccessToken } from '@partiu/api/axios';
import { IUser } from '@partiu/api/types';
import { useUpdateProfileMutation } from '@partiu/api/user/hooks';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface IProfileContext {
  profile: IUser | null;
  isLoading: boolean;
  updateProfile: (data: Record<string, string | boolean>) => void;
  logout: () => void;
}

const defaultProfileContextData: IProfileContext = {
  profile: null,
  updateProfile: () => {},
  logout: () => {},
  isLoading: false,
};

const ProfileContext = createContext(defaultProfileContextData);

export function ProfileProvider({ children }: PropsWithChildren) {
  const [data, setData] = useState(defaultProfileContextData);
  const onUpdateSuccess = useCallback(
    (profile: IUser | null) => {
      if (!profile) return;

      localStorage.setItem('profile', JSON.stringify(profile));
      setData((data) => ({ ...data, profile }));
    },
    [data]
  );
  const updateProfileMutation = useUpdateProfileMutation({
    onSuccess: onUpdateSuccess,
  });
  const isLoading = useMemo(() => updateProfileMutation.isLoading, [updateProfileMutation]);
  const updateProfile = useCallback(
    (data: Record<string, string | boolean>) => {
      setData((data) => ({
        ...data,
      }));
      updateProfileMutation.mutate(data);
    },
    [updateProfileMutation]
  );

  useEffect(() => {
    if (!isAuthenticated()) return;
    const savedProfile = JSON.parse(localStorage.getItem('profile') || 'null');
    if (savedProfile) setData((data) => ({ ...data, profile: savedProfile }));
    updateProfileMutation.mutate({});
  }, []);

  return (
    <ProfileContext.Provider
      value={{
        ...data,
        isLoading: isLoading,
        updateProfile,
        logout() {
          const existingUrlData = localStorage.getItem('urlData');

          setAccessToken(null);
          localStorage.clear();

          if (existingUrlData) localStorage.setItem('urlData', existingUrlData);

          window.location.reload();
        },
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}
